import React from "react";

/**
 * PriceContinuum — single-glance bar showing where price sits between
 * Stop ──── Entry ──── TP1 ──── Moon.  Dark / HUD variant — matches /mock/dark.
 *
 * Props:
 *   entry, stop, tp1, moon  numbers (alert prices)
 *   now                     current live price
 *   compact                 if true, render slightly smaller
 */
export default function PriceContinuum({ entry, stop, tp1, moon, now, compact = false }) {
  if (!(entry > 0 && stop > 0 && moon > entry)) return null;
  const _now = Number.isFinite(+now) ? +now : entry;
  const range = moon - stop || 1;
  const clamp = (x) => Math.max(0, Math.min(100, x));
  const nowPct = clamp(((_now - stop) / range) * 100);
  const entryPct = clamp(((entry - stop) / range) * 100);
  const tp1Pct = clamp(((tp1 - stop) / range) * 100);
  const ret = entry > 0 ? ((_now - entry) / entry) * 100 : 0;
  const positive = ret >= 0;
  const accent = positive ? "#4ADE80" : "#FF5C5C";

  const labelSize = compact ? "text-[9px]" : "text-[10px]";

  return (
    <div data-testid="price-continuum">
      <div
        style={{
          position: "relative",
          height: compact ? 5 : 6,
          borderRadius: 9999,
          overflow: "visible",
          background:
            "linear-gradient(to right, rgba(255,92,92,0.32), rgba(19,33,28,0.9) 50%, rgba(74,222,128,0.32))",
          border: "1px solid rgba(168, 85, 247, 0.18)",
        }}
      >
        {/* Entry tick */}
        <div
          title={`Entry $${entry}`}
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: `${entryPct}%`,
            width: 2,
            height: compact ? 12 : 14,
            backgroundColor: "#EEE9F5",
            opacity: 0.85,
          }}
        />
        {/* TP1 tick */}
        <div
          title={`TP1 $${tp1}`}
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: `${tp1Pct}%`,
            width: 2,
            height: compact ? 12 : 14,
            backgroundColor: "#4ADE80",
            boxShadow: "0 0 6px #4ADE80",
            opacity: 0.95,
          }}
        />
        {/* Now comet */}
        <div
          title={`Now $${_now.toFixed(2)}`}
          data-testid="price-continuum-now"
          style={{
            position: "absolute",
            top: compact ? -3 : -3,
            left: `calc(${nowPct}% - 6px)`,
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: accent,
            border: "2px solid #EEE9F5",
            boxShadow: `0 0 14px ${accent}, -22px 0 16px -8px ${accent}aa`,
          }}
        />
      </div>
      <div
        className={`mt-1.5 flex justify-between font-mono-data ${labelSize}`}
        style={{ color: "rgba(238, 233, 245, 0.45)", letterSpacing: "0.05em" }}
      >
        <span>STOP ${stop}</span>
        <span style={{ color: "#EEE9F5", fontWeight: 600 }}>ENTRY ${entry}</span>
        <span style={{ color: "#4ADE80" }}>TP1 ${tp1}</span>
        <span style={{ color: "#F5A623" }}>MOON ${moon}</span>
      </div>
    </div>
  );
}
